/* One owner for automatic, silent films. The collection hero owns its poster/frame
   transition; all other films start only while visible, including deferred media. */
(() => {
  if (window.JBAutoVideos) return;
  const records = new Map();
  const selector = 'video,iframe[src*="youtube.com/embed/"],iframe[src*="youtube-nocookie.com/embed/"],iframe[src*="player.vimeo.com/video/"],iframe[data-jb-video-src],iframe[data-src*="youtube.com/embed/"],iframe[data-src*="youtube-nocookie.com/embed/"],iframe[data-src*="player.vimeo.com/video/"]';
  let queued = false;
  let pageHidden = false;
  const originFor = frame => {
    try { return new URL(frame.src, location.href).origin; } catch (_) { return null; }
  };
  const prepare = video => {
    if (video.controls) video.controls = false;
    if (!video.muted) video.muted = true;
    if (!video.defaultMuted) video.defaultMuted = true;
    if (!video.loop) video.loop = true;
    if (!video.playsInline) video.playsInline = true;
    video.disablePictureInPicture = true;
    video.disableRemotePlayback = true;
    if (video.tabIndex !== -1) video.tabIndex = -1;
  };
  const eligible = (element, state) => {
    if (!element.isConnected || document.hidden || pageHidden || !state.inView) return false;
    if (element.closest('[hidden],[inert]')) return false;
    const cell = element.closest('.carousel__slide,.product-images__slide');
    if (cell?.closest('.flickity-enabled') && !cell.classList.contains('is-selected')) return false;
    if (!element.getClientRects().length) return false;
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  };
  const syncFrame = (frame, state, active) => {
    const deferred = frame.dataset.jbVideoSrc || frame.dataset.src;
    if (active && !frame.getAttribute('src') && deferred) frame.src = deferred;
    if (!frame.getAttribute('src') || state.lastActive === active) return;
    const origin = originFor(frame);
    if (!origin || !frame.contentWindow) return;
    const youtube = /youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname);
    const send = message => frame.contentWindow.postMessage(JSON.stringify(message), origin);
    if (youtube) {
      send({ event: 'command', func: 'mute', args: [] });
      send({ event: 'command', func: active ? 'playVideo' : 'pauseVideo', args: [] });
    } else {
      send({ method: 'setVolume', value: 0 });
      send({ method: 'setLoop', value: true });
      send({ method: active ? 'play' : 'pause' });
    }
    state.lastActive = active;
  };
  const sync = (element, state) => {
    if (element.tagName === 'VIDEO') prepare(element);
    if (element.closest('jb-hero-media')) return;
    const active = eligible(element, state);
    if (element.tagName === 'IFRAME') { syncFrame(element, state, active); return; }
    if (element.autoplay !== active) element.autoplay = active;
    if (!active) {
      if (state.pending) { clearTimeout(state.pending.timer); state.pending = null; }
      if (!element.paused) element.pause();
      return;
    }
    if (!element.paused || state.pending || state.blocked) return;
    const attempt = {};
    state.pending = attempt;
    attempt.timer = setTimeout(() => {
      if (state.pending !== attempt) return;
      state.pending = null;
      state.retryWhenReady = true;
    }, 20000);
    let play;
    try { play = element.play(); } catch (error) { play = Promise.reject(error); }
    Promise.resolve(play).then(() => {
      if (state.pending === attempt) { state.abortRetries = 0; state.retryWhenReady = false; }
    }).catch(error => {
      if (state.pending !== attempt) return;
      if (error?.name === 'AbortError' && !state.abortRetries) {
        state.abortRetries = 1;
        attempt.retry = true;
      } else if (eligible(element, state)) state.blocked = true;
    }).finally(() => {
      clearTimeout(attempt.timer);
      if (state.pending === attempt) state.pending = null;
      if (records.get(element) !== state) return;
      if (!eligible(element, state)) element.pause();
      else if (attempt.retry) refresh();
    });
  };
  const intersection = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = records.get(entry.target);
      if (!state) return;
      state.inView = entry.isIntersecting;
      sync(entry.target, state);
    });
  }, { threshold: 0.05 }) : null;
  const register = element => {
    if (records.has(element) || element.closest('jb-category-media,jb-hero-media')) return;
    const state = { inView: !intersection, abort: new AbortController(), blocked: false };
    records.set(element, state);
    const { signal } = state.abort;
    element.tabIndex = -1;
    if (element.tagName === 'VIDEO') {
      prepare(element);
      element.addEventListener('volumechange', () => prepare(element), { signal });
      element.addEventListener('contextmenu', event => event.preventDefault(), { signal });
      element.addEventListener('keydown', event => event.preventDefault(), { signal });
      element.addEventListener('ended', () => {
        if (element.closest('jb-hero-media')) return;
        if (eligible(element, state)) { element.currentTime = 0; sync(element, state); }
      }, { signal });
      element.addEventListener('error', () => { state.blocked = true; }, { signal });
      element.addEventListener('pause', () => {
        if (!eligible(element, state) || state.blocked || state.pending) return;
        clearTimeout(state.resumeTimer);
        state.resumeTimer = setTimeout(() => sync(element, state), 250);
      }, { signal });
      element.addEventListener('emptied', () => {
        if (state.pending) clearTimeout(state.pending.timer);
        state.pending = null;
        state.blocked = false;
        state.retryWhenReady = false;
        state.abortRetries = 0;
        sync(element, state);
      }, { signal });
      ['loadeddata', 'canplay'].forEach(type => element.addEventListener(type, () => {
        if (state.retryWhenReady && !element.error) { state.blocked = false; state.retryWhenReady = false; }
        sync(element, state);
      }, { signal }));
      element.addEventListener('play', () => {
        prepare(element);
        if (!element.closest('jb-hero-media') && !eligible(element, state)) element.pause();
      }, { signal });
    } else {
      element.removeAttribute('allowfullscreen');
      element.addEventListener('load', () => { state.lastActive = null; sync(element, state); }, { signal });
    }
    intersection?.observe(element);
    sync(element, state);
  };
  const cleanup = () => {
    records.forEach((state, element) => {
      if (element.isConnected) return;
      state.abort.abort();
      clearTimeout(state.resumeTimer);
      intersection?.unobserve(element);
      if (state.pending) clearTimeout(state.pending.timer);
      if (element.tagName === 'VIDEO') element.pause();
      records.delete(element);
    });
  };
  const scan = root => {
    if (root.matches?.(selector)) register(root);
    root.querySelectorAll?.(selector).forEach(register);
  };
  const refresh = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      cleanup();
      records.forEach((state, element) => sync(element, state));
    });
  };
  window.JBAutoVideos = { refresh };
  // Transform/opacity animation frames do not change playback eligibility.
  // Preserve other inline styles so display/visibility and CSS variables still
  // trigger a check, including when an animated ancestor also becomes hidden.
  const visibilityStyle = value => (value || '').split(';').map(part => part.trim())
    .filter(part => part && !/^(?:-webkit-)?(?:transform|transform-origin|opacity|translate|rotate|scale|will-change)\s*:/.test(part)).join(';');
  const mutation = new MutationObserver(changes => {
    let relevant = false;
    for (const change of changes) {
      if (change.type === 'childList') {
        const previousSize = records.size;
        change.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
        if (records.size !== previousSize) relevant = true;
        if (change.removedNodes.length) {
          for (const element of records.keys()) { if (!element.isConnected) { relevant = true; break; } }
        }
      } else {
        if (change.attributeName === 'style' && visibilityStyle(change.oldValue) === visibilityStyle(change.target.getAttribute('style'))) continue;
        for (const element of records.keys()) {
          if (change.target === element || change.target.contains(element)) { relevant = true; break; }
        }
      }
    }
    if (relevant) refresh();
  });
  mutation.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeOldValue: true, attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden'] });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) records.forEach((state, element) => sync(element, state));
    else refresh();
  });
  window.addEventListener('resize', refresh, { passive: true });
  window.addEventListener('pagehide', () => { pageHidden = true; records.forEach((state, element) => sync(element, state)); });
  window.addEventListener('pageshow', () => { pageHidden = false; refresh(); });
  const resumeVisible = () => {
    records.forEach((state, element) => {
      if (!eligible(element, state)) return;
      if (element.tagName === 'VIDEO' && element.error && [1, 2].includes(element.error.code)) element.load();
      if (!element.error) { state.blocked = false; sync(element, state); }
    });
  };
  ['click', 'touchend', 'keydown'].forEach(type => document.addEventListener(type, resumeVisible, { capture: true, passive: true }));
  window.addEventListener('online', resumeVisible);
  window.addEventListener('focus', resumeVisible);
  window.addEventListener('pageshow', resumeVisible);
  scan(document);
})();
